import liff from '@line/liff'

/**
 * 現在のパスが LIFF 経由かどうか。
 * LIFF は liff.state でパスを復元するため、その痕跡か /line/ 配下かで判定する。
 *
 * NOTE: 現状どこからも呼ばれていない。
 * LIFF内/外で挙動を出し分ける必要が出た時のために残す（テストのみが参照）。
 */
export function isInLiff(fullPath) {
  if (typeof fullPath !== 'string' || !fullPath) return false
  if (fullPath.includes('liff.state')) return true
  // '/lineup/' のような別ページを誤検知しないよう、区切りまで見る
  return fullPath === '/line' || fullPath.startsWith('/line/') || fullPath.startsWith('/line?')
}

/**
 * 本部UID の形式チェック。
 *
 * `liff.state` の先頭セグメントをそのまま本部UIDとして扱うと、
 * `//evil.com/` のような値まで拾ってしまう。想定する文字種に限定する。
 * 実データは `HQ_7911635233` のような英数字＋アンダースコア。
 */
const HQ_UID_PATTERN = /^[0-9a-zA-Z_-]+$/

/**
 * vue-router のクエリ値を文字列に正規化する。
 * 同名クエリ（?a=1&a=2）は配列で来るため、その場合は先頭を採用する。
 */
function firstQueryValue(value) {
  const raw = Array.isArray(value) ? value[0] : value
  return typeof raw === 'string' ? raw : null
}

/**
 * `liff.state` を「本部UID」と「遷移先パス」に分解する。
 *
 * 実機で確定した LIFF の挙動（2026-09-21 採取・2026-10-30 本番で再確認）:
 *   リッチメニュー  https://miniapp.line.me/<LIFF_ID>/HQ_7911635233/
 *   → 一次          /line/?liff.state=%2FHQ_7911635233%2F#context_token=...
 *   → 二次          /line/HQ_7911635233/   ← liff.init() が移す（LIFF の仕様）
 *
 * LIFF はリッチメニューURLの残りをまず `liff.state` に畳んでエンドポイントURL
 * （/line/）へ飛ばし、`liff.init()` が「エンドポイントURL＋liff.state のパス」へ
 * 二次リダイレクトする。そのため入口は2本ある:
 *   - 一次: pages/line/index.vue が `liff.state` を読む（このページで init が走る）
 *   - 二次: pages/line/_.vue が パス（parseLinePath）を読む
 * 一次で liff.state を自前で読むのは、init の前に本部UID（＝LIFF ID）が要るため。
 *
 * 値はパス文字列（例 `/HQ_7911635233/reserve/lesson/`）。
 * URLエンコードされて届くが vue-router がデコード済みで渡してくる。
 *
 * 先頭セグメント = 本部UID / 残り = 遷移先パス。
 * 本部UIDが HQ_UID_PATTERN に合わない場合（`//evil.com/` など）は
 * 丸ごと無効として null を返す（遷移先だけ拾って悪用されないように）。
 *
 * @param {Object} query $route.query 相当
 * @returns {{hqUid: String, to: String|null}|null} 分解結果。読めなければ null
 */
export function parseLiffState(query) {
  if (!query) return null
  const state = firstQueryValue(query['liff.state'])
  if (!state || !state.startsWith('/')) return null

  // '/HQ_x/reserve/lesson/' -> ['HQ_x', 'reserve', 'lesson', '']
  const segments = state.slice(1).split('/')
  const hqUid = segments[0]
  if (!hqUid || !HQ_UID_PATTERN.test(hqUid)) return null

  const rest = segments.slice(1).join('/')
  // 残りが空（'/HQ_x/' や '/HQ_x'）なら遷移先の指定なし
  return { hqUid, to: rest === '' ? null : `/${rest}` }
}

/**
 * パス方式（/line/<本部UID>/<遷移先>）を「本部UID」と「遷移先パス」に分解する。
 *
 * `liff.init()` の二次リダイレクト先（parseLiffState のコメント参照）。
 * pages/line/_.vue（Nuxt の catch-all `/line/*`）が受け、
 * `$route.params.pathMatch` に `<本部UID>/<遷移先>` が先頭スラッシュなしで入る。
 * 既存の静的ページ（/line/unlock/ など）は Nuxt が catch-all より先に並べるため、こちらには来ない。
 *
 * 本部UIDが HQ_UID_PATTERN に合わなければ丸ごと null（parseLiffState と同じ理由）。
 * 遷移先にクエリは含まれない（vue-router が $route.query に分離する。再結合は extractRedirectPath）。
 *
 * @param {Object} params $route.params 相当（{ pathMatch }）
 * @returns {{hqUid: String, to: String|null}|null} 分解結果。読めなければ null
 */
export function parseLinePath(params) {
  const raw = params && params.pathMatch
  if (typeof raw !== 'string' || !raw) return null

  const segments = (raw.startsWith('/') ? raw.slice(1) : raw).split('/')
  const hqUid = segments[0]
  if (!hqUid || !HQ_UID_PATTERN.test(hqUid)) return null

  const rest = segments.slice(1).join('/')
  return { hqUid, to: rest === '' ? null : `/${rest}` }
}

/**
 * リッチメニューの URL から hq（本部uid）を取り出す。
 *
 * 優先順位:
 *   1. `liff.state` の先頭セグメント ← LIFF の一次リダイレクト（/line/?liff.state=…）
 *   2. パス `/line/<本部UID>/…`     ← liff.init() の二次リダイレクト（pages/line/_.vue）
 *   3. クエリ `?hq=`（旧方式・後方互換）
 *
 * @param {Object} query $route.query 相当
 * @param {Object} [params] $route.params 相当（{ pathMatch }）。pages/line/_.vue だけが持つ
 * @returns {String|null} 本部UID。取れなければ null
 */
export function extractHqUid(query, params) {
  const state = parseLiffState(query)
  if (state) return state.hqUid

  const fromPath = parseLinePath(params)
  if (fromPath) return fromPath.hqUid

  if (!query) return null
  const raw = firstQueryValue(query.hq)
  if (raw === null) return null
  return raw ? raw : null
}

/**
 * パス方式で遷移先に引き継がない（＝この入口・LINE・LIFF が使う）クエリキー。
 *
 * 引き継ぐ側をホワイトリストにしないのは、遷移先が何を受け取るかをこの入口が
 * 知り得ないため（`selectedMenuUids` のような画面固有のクエリは今後も増える）。
 * 落とし漏れても無害なクエリが1つ余計に付くだけだが、ホワイトリストだと
 * 利用者指定のクエリが黙って消える。
 *
 *   hq / to        … 旧方式のクエリ（本部UID・遷移先）。この入口が消費する
 *   code / state   … LINE ログインの OAuth コールバックが付ける
 *   liffClientId / liffRedirectUri / liffLanguage
 *                  … LIFF SDK がリダイレクトで付ける内部パラメータ（2026-09-23 実機で確認）
 *   liff.*         … liff.state / liff.referrer など LIFF のパラメータ（前方一致で落とす）
 */
const CONSUMED_QUERY_KEYS = ['hq', 'to', 'code', 'state', 'liffClientId', 'liffRedirectUri', 'liffLanguage']

function isConsumedQueryKey(key) {
  return CONSUMED_QUERY_KEYS.includes(key) || key.startsWith('liff.')
}

/**
 * 遷移先に引き継ぐクエリを `?a=1&b=2` 形式で返す（無ければ ''）。
 * 同名クエリ（配列）は全件展開し、値なしクエリ（vue-router では null）は空文字にする。
 */
function buildForwardedQuery(query) {
  if (!query || typeof query !== 'object') return ''

  const search = new URLSearchParams()
  Object.keys(query).forEach((key) => {
    if (isConsumedQueryKey(key)) return
    const value = query[key]
    const values = Array.isArray(value) ? value : [value]
    values.forEach((v) => {
      search.append(key, v === null || v === undefined ? '' : String(v))
    })
  })

  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

/**
 * 遷移先パスをアプリ内パスに限定する（オープンリダイレクト対策）。
 *
 * `to` はリッチメニューの URL 由来＝外部から自由に差し込める値なので、
 * 絶対URL・プロトコル相対URL・スキームを弾いて、必ず自サイト内に閉じる。
 */
export function safeRedirectPath(to) {
  if (typeof to !== 'string' || !to) return '/'
  if (!to.startsWith('/')) return '/'
  // '//evil.com'（プロトコル相対）と '/\evil.com'（ブラウザが // と解釈しうる）を弾く
  if (to.startsWith('//') || to.startsWith('/\\')) return '/'
  return to
}

/**
 * 遷移先（to）を取り出す。
 *
 * extractHqUid と同じ優先順位:
 *   1. `liff.state` の本部UIDより後ろ ← LIFF の一次リダイレクト
 *   2. パス `/line/<本部UID>/` より後ろ ← liff.init() の二次リダイレクト
 *   3. クエリ `?to=`（旧方式・後方互換）
 *
 * いずれも空ならホーム（'/'）。
 * 戻り値は必ず safeRedirectPath を通したアプリ内パス（オープンリダイレクト対策）。
 *
 * `liff.state` はパスとクエリを含んだ1本の文字列で届くため、
 * `?selectedMenuUids=` のような遷移先画面向けのクエリはそのまま保持される。
 * パス方式は vue-router がパスとクエリを分離するため、ここで再結合する
 * （入口・LINE・LIFF が使うクエリは除く）。
 *
 * @param {Object} query $route.query 相当
 * @param {Object} [params] $route.params 相当（{ pathMatch }）
 * @returns {String} アプリ内パス
 */
export function extractRedirectPath(query, params) {
  const state = parseLiffState(query)
  if (state && state.to) return safeRedirectPath(state.to)

  // liff.state が無いときだけパスを見る（liff.state があればそちらの指定が正）
  const fromPath = state ? null : parseLinePath(params)
  if (fromPath && fromPath.to) {
    const safe = safeRedirectPath(fromPath.to)
    // 弾かれてホームになったらクエリも付けない（遷移先の指定が無かったのと同じ扱い）
    if (safe === '/') return '/'
    return safeRedirectPath(safe + buildForwardedQuery(query))
  }

  // liff.state・パスに遷移先の続きが無い場合のみクエリを見る（後方互換）
  return safeRedirectPath(firstQueryValue(query && query.to))
}

/**
 * LINE の IDトークンをログイン画面へ引き継ぐための sessionStorage キー。
 *
 * IDトークンは本人性の証明そのもので、有効期限内なら紐付けAPIを叩ける。
 * URL クエリに載せるとブラウザ履歴・Referer・アクセスログ・LINE WebView の
 * 内部ログに残るため、同一タブ内に閉じた sessionStorage で受け渡す。
 */
const LINE_ID_TOKEN_KEY = 'line_id_token'

/**
 * IDトークンを sessionStorage に預ける（ログイン画面へ引き継ぐため）。
 *
 * プライベートブラウズや WebView の制限で sessionStorage が使えない／
 * 書き込みが例外になる環境があるため、必ず握って false を返す。
 * 失敗しても「紐付けされないだけ」でログイン自体は成立する。
 *
 * @returns {Boolean} 預けられたか
 */
export function stashLineIdToken(idToken) {
  if (typeof idToken !== 'string' || !idToken) return false
  try {
    window.sessionStorage.setItem(LINE_ID_TOKEN_KEY, idToken)
    return true
  } catch (e) {
    return false
  }
}

/**
 * 預けた IDトークンを取り出して消す（使い捨て）。
 *
 * 取り出し・削除のどちらが失敗しても null を返すだけに留める。
 * LINE の WebView では sessionStorage が揮発しうるが、その場合は
 * 「紐付けがスキップされる」だけでログインは通る。
 *
 * @returns {String|null} IDトークン。取れなければ null
 */
export function takeLineIdToken() {
  let token = null
  try {
    token = window.sessionStorage.getItem(LINE_ID_TOKEN_KEY)
  } catch (e) {
    return null
  }
  try {
    window.sessionStorage.removeItem(LINE_ID_TOKEN_KEY)
  } catch (e) {
    // 削除できなくても読み出しは済んでいるので続行する
  }
  return token || null
}

/**
 * 遷移先パスを LINE ログインのリダイレクトまたぎで保持するための sessionStorage キー。
 *
 * 背景（2026-09-23 実機で確定）:
 * `liff.init()` は未ログイン時に LINE 認証へ飛ばすが、その `redirect_uri` には
 * LIFF に登録した「エンドポイントURL」（/line/）しか使わない。
 * そのため認証から戻ってきた時点で、遷移先 `?liff.state=/<本部UID>/reserve/lesson/`
 * もクエリ `?selectedMenuUids=...` も URL から消えうる。
 *
 * 通常は LIFF 側が `liff.state` を復元してくれるが、それに頼り切ると
 * 復元されなかった時に黙ってホームへ飛ばされる。そこで初期化の直前に
 * 遷移先を預け、戻ってきた後に取り出す（保険）。
 */
const LINE_REDIRECT_PATH_KEY = 'line_redirect_path'

/**
 * 遷移先パスを sessionStorage に預ける（LINE 認証のリダイレクトまたぎ用）。
 *
 * 預ける値は必ず safeRedirectPath を通す。ここに入るのはリッチメニューURL由来＝
 * 外部から差し込める値なので、保存時点で自サイト内パスに閉じておく（多層防御）。
 * ホーム（'/'）は「遷移先の指定なし」と同じなので預けない。
 *
 * sessionStorage が使えない環境では黙って false を返す（遷移先が
 * 引き継がれずホームへ行くだけで、ログイン自体は成立する）。
 *
 * @returns {Boolean} 預けられたか
 */
export function stashLineRedirectPath(to) {
  const safe = safeRedirectPath(to)
  if (safe === '/') return false
  try {
    window.sessionStorage.setItem(LINE_REDIRECT_PATH_KEY, safe)
    return true
  } catch (e) {
    return false
  }
}

/**
 * 預けた遷移先パスを取り出して消す（使い捨て）。
 *
 * 取り出した値も safeRedirectPath を通す。sessionStorage は同一オリジンの
 * 別スクリプトからも書けるため、書き込み時の検証だけに頼らない（多層防御）。
 *
 * 読み出し・削除のどちらが失敗しても null を返すだけに留める。
 *
 * @returns {String|null} アプリ内パス。無い・読めない・安全でないなら null
 */
export function takeLineRedirectPath() {
  let path = null
  try {
    path = window.sessionStorage.getItem(LINE_REDIRECT_PATH_KEY)
  } catch (e) {
    return null
  }
  try {
    window.sessionStorage.removeItem(LINE_REDIRECT_PATH_KEY)
  } catch (e) {
    // 削除できなくても読み出しは済んでいるので続行する
  }
  if (!path) return null
  const safe = safeRedirectPath(path)
  return safe === '/' ? null : safe
}

/**
 * LIFF ID をタブのセッション内で使い回すための sessionStorage キー（本部ごと）。
 *
 * 負荷監査（2026-09-27）: 入口を開くたびに config.json を呼んでいた。
 * LIFF ID は本部の設定値でめったに変わらず、公開値（リッチメニューURLに載る）なので
 * sessionStorage に置いてよい。タブを閉じれば消えるため、設定変更も次のタブで反映される。
 */
const LIFF_ID_KEY_PREFIX = 'line_liff_id:'

/**
 * 使い回せる LIFF ID を返す。無い・読めないなら null。
 */
export function getCachedLiffId(hqUid) {
  if (!hqUid) return null
  try {
    return window.sessionStorage.getItem(LIFF_ID_KEY_PREFIX + hqUid) || null
  } catch (e) {
    return null
  }
}

/**
 * LIFF ID を預ける。sessionStorage が使えなければ黙って諦める（毎回取得になるだけ）。
 */
export function cacheLiffId(hqUid, liffId) {
  if (!hqUid || !liffId) return
  try {
    window.sessionStorage.setItem(LIFF_ID_KEY_PREFIX + hqUid, liffId)
  } catch (e) {
    // 使い回せないだけで動作には影響しない
  }
}

/**
 * 会員アプリの auth_token（JWT）の payload にある expire_at を返す。
 * サーバの JsonWebToken.encode は標準の exp ではなく expire_at（ISO8601文字列）を入れている。
 * 署名はここでは検証しない（有効性の最終判断はサーバ。これは「確認する価値があるか」の足切り）。
 *
 * @returns {Date|null} 読めなければ null
 */
export function authTokenExpireAt(authToken) {
  if (typeof authToken !== 'string') return null
  // Authorization ヘッダの "Bearer xxx" 形式でも読めるようにする（サーバも末尾を使う）
  const payload = decodeJwtPayload(authToken.trim().split(' ').pop())
  if (!payload) return null
  const expireAt = new Date(payload.expire_at)
  return Number.isNaN(expireAt.getTime()) ? null : expireAt
}

/**
 * JWT の payload を読む（署名は検証しない）。読めなければ null。
 */
function decodeJwtPayload(jwt) {
  if (typeof jwt !== 'string') return null
  const parts = jwt.split('.')
  if (parts.length !== 3) return null
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const payload = JSON.parse(window.atob(padded))
    return payload && typeof payload === 'object' ? payload : null
  } catch (e) {
    return null
  }
}

/**
 * LINE の IDトークン（liff.getIDToken()）から LINE ユーザーID（payload の sub）を取り出す。
 * liff.getDecodedIDToken().sub と同じ値。署名はここでは検証しない
 * （本人性の最終判断はサーバの session.json。これは「前回と同じ LINE ユーザーか」の足切り）。
 *
 * @returns {String|null} 読めなければ null
 */
export function lineUserIdFromIdToken(idToken) {
  const payload = decodeJwtPayload(idToken)
  if (!payload) return null
  return typeof payload.sub === 'string' && payload.sub ? payload.sub : null
}

/**
 * LINE 入口で既存のログイン状態を使い回してよいか（サーバ確認の前段）。
 * 同じ本部で、期限内のトークンを持っていて、そのログインが LINE 入口で作られたもの
 * （lineSession の印が同じ本部・同じ会員）のときだけ true。
 * 通常ログインのセッションは LINE に紐付いた会員と一致するか分からない
 * （家族の端末・スタッフの代理ログイン・LINE の WebView で通常ログインだけ済ませて未紐付け）ので、
 * LINE 認証（session.json）で本人を確かめ直す（2026-09-28 レビュー cli-flow M3）
 * さらに印の LINE ユーザーID が今の LIFF の LINE ユーザーと一致するときだけ true。
 * 同じ端末で LINE アカウントを切り替えたときに前の LINE ユーザーの会員へ進まないため
 * （2026-09-28 Codex 最終レビュー）。どちらかが取れなければ使い回さない。
 *
 * @param {Object} authState  store.state.auth（authToken / headquarter / client / lineSession）
 * @param {String} hqUid      入口URLの本部UID
 * @param {String} lineUserId 今の LIFF の LINE ユーザーID（lineUserIdFromIdToken）
 * @param {Number} now        現在時刻（ms）。テスト用
 */
export function hasReusableLineSession(authState, hqUid, lineUserId, now = Date.now()) {
  if (!authState || !authState.authToken || !hqUid) return false
  if (!authState.headquarter || authState.headquarter.uid !== hqUid) return false
  const mark = authState.lineSession
  if (!mark || mark.hq_uid !== hqUid) return false
  if (!authState.client || !authState.client.uid || mark.client_uid !== authState.client.uid) return false
  if (!lineUserId || !mark.line_user_id || mark.line_user_id !== lineUserId) return false
  const expireAt = authTokenExpireAt(authState.authToken)
  return !!expireAt && expireAt.getTime() > now
}

/**
 * LIFF を初期化して ID トークンを取得する。
 * 未ログインなら liff.login() に飛ばす（この場合 null を返す前に画面遷移する）。
 */
export async function initLiffAndGetIdToken(liffId) {
  await liff.init({ liffId })

  if (!liff.isLoggedIn()) {
    liff.login()
    return null
  }

  return liff.getIDToken()
}
